import { useState } from "react";

import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import { Button, CircularProgress, colors, Tooltip } from "@mui/material";

import { FlaggingAnalyticsService, UserAnalyticsService } from "@analytics/index";

import { PopupActions } from "@store/popupStore";

import theme from "@theme/index";

interface IWebsiteLinkWithFallbackProperties {
  color?: string;
  company?: string;
  websiteNakedDomain?: string;
  workflow: string;
}

/**
 * This component is used to render a button that will check if a website is available and then redirect to it.
 * If the website is not available, it will flag the company for review.
 *
 * @param websiteNakedDomain - The website domain without the protocol (http/https).
 * @param company - The company name.
 * @param workflow - The workflow name.
 * @param color - The color of the button.
 */
export default function WebsiteLinkWithFallback({
  color = "primary",
  company = "",
  websiteNakedDomain = "",
  workflow,
}: IWebsiteLinkWithFallbackProperties) {
  const [isLoading, setLoading] = useState(false);

  let buttonStyles = {};
  if (color === "white") {
    buttonStyles = {
      "&:hover": {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.primary.dark,
      },
      backgroundColor: colors.grey[100],
      boxShadow: "none",
      color: theme.palette.primary.dark,
    };
  }

  const flagURL = () => {
    FlaggingAnalyticsService.dataIssueFlagged(
      websiteNakedDomain,
      "website_timeout",
      "failed_website_check",
      workflow,
      true,
    );
    PopupActions.openAlert({
      duration: 4000,
      message: "This company may no longer be active. We've flagged it for review.",
      severity: "info",
    });
  };

  const openURL = (url: string) => {
    setLoading(true);
    if (url) {
      window.open(`//${websiteNakedDomain}`, "_blank", "noopener,noreferrer");
      UserAnalyticsService.trackOutboundClick(url, "company_website");
    } else {
      flagURL();
    }
    setLoading(false);
  };

  let buttonContent;

  if (isLoading) {
    buttonContent = <CircularProgress size={24} sx={{ color: theme.palette.grey[500] }} />;
  } else if (color === "white") {
    buttonContent = "Visit Website";
  } else {
    buttonContent = <LanguageRoundedIcon sx={{ color: theme.palette.info.main }} />;
  }

  return (
    <Tooltip placement="bottom" title={isLoading ? "Checking website availability..." : "Visit company website."}>
      <span>
        <Button
          color="primary"
          disabled={isLoading}
          onClick={() => openURL(websiteNakedDomain)}
          size="small"
          sx={buttonStyles}
          variant="text"
        >
          {buttonContent}
        </Button>
      </span>
    </Tooltip>
  );
}
